import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { mensagemBanco } from '../mensagemBanco';
import ModalQRPresenca from './ModalQRPresenca';
import ModalConfirmarAcao from './ModalConfirmarAcao';
import PresencasFormacao from './PresencasFormacao';
import Frequencia from './Frequencia';
import DicaPrimeiraVez from '@/components/feature/DicaPrimeiraVez';
import type { FormacaoPresenca } from '../types';

interface Props {
  eventoId: number;
}

type Estado = 'carregando' | 'pronto' | 'erro';

const DIAS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

function formatarQuando(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const dia = DIAS[d.getDay()];
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  const hora = min === '00' ? `${hh}h` : `${hh}h${min}`;
  return `${dia} ${dd}/${mm} · ${hora}`;
}

function corSituacao(situacao: string | null): string {
  const v = (situacao || '').toLowerCase();
  if (v.includes('acontecendo')) return 'bg-green-100 text-green-700';
  if (v.includes('cancelada')) return 'bg-red-100 text-red-700';
  if (v.includes('conferida')) return 'bg-blue-100 text-blue-700';
  if (v.includes('encerrada') || v.includes('pendente')) return 'bg-amber-100 text-amber-700';
  return 'bg-secondary-100 text-secondary-600';
}

function SeletorVisao({
  visao,
  aoMudar,
}: {
  visao: 'formacao' | 'frequencia';
  aoMudar: (v: 'formacao' | 'frequencia') => void;
}) {
  const opcoes: { valor: 'formacao' | 'frequencia'; rotulo: string }[] = [
    { valor: 'formacao', rotulo: 'Por formação' },
    { valor: 'frequencia', rotulo: 'Frequência' },
  ];
  return (
    <div className="flex w-full rounded-full bg-secondary-100 p-1 sm:max-w-[360px]">
      {opcoes.map((o) => (
        <button
          key={o.valor}
          type="button"
          onClick={() => aoMudar(o.valor)}
          className={`flex-1 cursor-pointer whitespace-nowrap rounded-full px-3 py-2 text-sm font-semibold transition-colors ${
            visao === o.valor
              ? 'bg-white text-foreground-950 shadow-sm'
              : 'text-foreground-600 hover:text-foreground-900'
          }`}
        >
          {o.rotulo}
        </button>
      ))}
    </div>
  );
}

export default function Presencas({ eventoId }: Props) {
  const [estado, setEstado] = useState<Estado>('carregando');
  const [erro, setErro] = useState<string | null>(null);
  const [formacoes, setFormacoes] = useState<FormacaoPresenca[]>([]);

  const [aberta, setAberta] = useState<FormacaoPresenca | null>(null);
  const [qrPara, setQrPara] = useState<FormacaoPresenca | null>(null);
  const [pedindoNovo, setPedindoNovo] = useState<FormacaoPresenca | null>(null);
  const [gerando, setGerando] = useState(false);

  const [cancelarPara, setCancelarPara] = useState<FormacaoPresenca | null>(null);
  const [cancelando, setCancelando] = useState(false);

  const [conferirPara, setConferirPara] = useState<FormacaoPresenca | null>(null);
  const [conferindo, setConferindo] = useState(false);

  const [erroAcao, setErroAcao] = useState<string | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const visao: 'formacao' | 'frequencia' =
    searchParams.get('visao') === 'frequencia' ? 'frequencia' : 'formacao';

  function trocarVisao(nova: 'formacao' | 'frequencia') {
    const next = new URLSearchParams(searchParams);
    if (nova === 'frequencia') next.set('visao', 'frequencia');
    else next.delete('visao');
    setSearchParams(next, { replace: true });
  }

  const seletor = <SeletorVisao visao={visao} aoMudar={trocarVisao} />;

  async function carregar() {
    const { data, error } = await supabase.rpc('resumo_presencas', {
      p_evento: eventoId,
    });
    if (error) throw error;
    const lista = (data as FormacaoPresenca[]) || [];
    lista.sort(
      (a, b) => new Date(a.quando).getTime() - new Date(b.quando).getTime(),
    );
    setFormacoes(lista);
  }

  useEffect(() => {
    if (visao !== 'formacao') return;
    let ativo = true;
    (async () => {
      setEstado('carregando');
      setErro(null);
      try {
        await carregar();
        if (ativo) setEstado('pronto');
      } catch (e) {
        if (ativo) {
          setErro(mensagemBanco(e));
          setEstado('erro');
        }
      }
    })();
    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventoId, visao]);

  async function gerarNovoQR() {
    if (!pedindoNovo) return;
    setGerando(true);
    setErroAcao(null);
    try {
      const { data, error } = await supabase.rpc('novo_qr_presenca', {
        p_encontro: pedindoNovo.encontro_id,
      });
      if (error) throw error;
      const resp = data as { ok: boolean; erro?: string } | null;
      if (!resp || resp.ok === false) {
        setErroAcao(resp?.erro || 'Não foi possível gerar o QR. Tente de novo.');
        return;
      }
      await carregar();
      setPedindoNovo(null);
      setQrPara(null);
    } catch (e) {
      setErroAcao(mensagemBanco(e));
    } finally {
      setGerando(false);
    }
  }

  async function cancelarFormacao() {
    if (!cancelarPara) return;
    setCancelando(true);
    setErroAcao(null);
    try {
      const { data, error } = await supabase.rpc('cancelar_formacao', {
        p_encontro: cancelarPara.encontro_id,
        p_cancelar: true,
      });
      if (error) throw error;
      const resp = data as { ok: boolean; erro?: string } | null;
      if (!resp || resp.ok === false) {
        setErroAcao(resp?.erro || 'Não foi possível cancelar. Tente de novo.');
        return;
      }
      await carregar();
      setCancelarPara(null);
    } catch (e) {
      setErroAcao(mensagemBanco(e));
    } finally {
      setCancelando(false);
    }
  }

  async function conferirFormacao() {
    if (!conferirPara) return;
    setConferindo(true);
    setErroAcao(null);
    try {
      const { data, error } = await supabase.rpc('conferir_formacao', {
        p_encontro: conferirPara.encontro_id,
      });
      if (error) throw error;
      const resp = data as { ok: boolean; erro?: string } | null;
      if (!resp || resp.ok === false) {
        setErroAcao(
          resp?.erro || 'Não foi possível fechar a conferência. Tente de novo.',
        );
        return;
      }
      await carregar();
      setConferirPara(null);
    } catch (e) {
      setErroAcao(mensagemBanco(e));
    } finally {
      setConferindo(false);
    }
  }

  if (aberta) {
    return (
      <PresencasFormacao
        encontroId={aberta.encontro_id}
        formacao={aberta.formacao}
        formacaoQuando={aberta.quando}
        aoVoltar={() => setAberta(null)}
      />
    );
  }

  if (visao === 'frequencia') {
    return (
      <div>
        <div className="mb-5">{seletor}</div>
        <Frequencia eventoId={eventoId} />
      </div>
    );
  }

  if (estado === 'carregando') {
    return (
      <div>
        <div className="mb-5">{seletor}</div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-44 animate-pulse rounded-lg bg-secondary-100" />
          ))}
        </div>
      </div>
    );
  }

  if (estado === 'erro') {
    return (
      <div>
        <div className="mb-5">{seletor}</div>
        <div className="rounded-lg border border-secondary-200 bg-white p-10 text-center">
          <p className="text-[15px] text-foreground-800">{erro}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-4 cursor-pointer whitespace-nowrap rounded-md border border-secondary-300 bg-white px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
          >
            Tentar de novo
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-5">{seletor}</div>
      {erroAcao && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {erroAcao}
        </div>
      )}

      {formacoes.length === 0 ? (
        <div className="rounded-lg border border-secondary-200 bg-white p-10 text-center">
          <p className="text-[15px] text-foreground-800">Nenhuma formação com presença.</p>
          <DicaPrimeiraVez id="painel.encontros.vazio" eventoId={eventoId} className="mt-5 text-left" />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {formacoes.map((f) => {
            const jaAconteceu = new Date(f.quando).getTime() <= Date.now();
            const podeCancelar = !f.cancelada && !f.conferida;
            const podeConferir =
              jaAconteceu && !f.conferida && !f.cancelada && f.aguardando === 0;
            return (
              <div
                key={f.encontro_id}
                className="flex min-w-0 flex-col rounded-3xl bg-white p-4 shadow-[0_6px_18px_rgba(20,25,60,.06)]"
              >
                <div className="flex items-start justify-between gap-3">
                  <h3 className="min-w-0 text-[15px] font-medium text-foreground-950">
                    {f.formacao}
                  </h3>
                  {f.na_janela && (
                    <span className="flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-700">
                      <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                      AGORA
                    </span>
                  )}
                </div>

                {f.situacao && (
                  <span
                    className={`mt-2 inline-flex max-w-full whitespace-normal rounded-full px-2.5 py-1 text-xs font-medium leading-tight ${corSituacao(
                      f.situacao,
                    )}`}
                  >
                    {f.situacao}
                  </span>
                )}

                <p className="mt-2 text-sm text-secondary-500">
                  {formatarQuando(f.quando)}
                  {f.local_nome ? ` · ${f.local_nome}` : ''}
                </p>

                <div className="mt-4 grid grid-cols-3 gap-2">
                  <div className="min-w-0 rounded-md bg-secondary-50 px-2 py-2 text-center">
                    <span className="block text-xl font-semibold text-foreground-950">
                      {f.confirmadas}
                    </span>
                    <span className="block text-[11px] leading-tight text-secondary-500">
                      confirmadas
                    </span>
                  </div>
                  <div className="min-w-0 rounded-md bg-secondary-50 px-2 py-2 text-center">
                    <span
                      className={`block text-xl font-semibold ${
                        f.aguardando > 0 ? 'text-amber-600' : 'text-foreground-950'
                      }`}
                    >
                      {f.aguardando}
                    </span>
                    <span className="block text-[11px] leading-tight text-secondary-500">
                      aguardando
                    </span>
                  </div>
                  <div className="min-w-0 rounded-md bg-secondary-50 px-2 py-2 text-center">
                    <span className="block text-xl font-semibold text-foreground-950">
                      {f.faltas}
                    </span>
                    <span className="block text-[11px] leading-tight text-secondary-500">
                      faltas
                    </span>
                  </div>
                </div>

                <div className="mt-auto pt-4">
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setAberta(f)}
                      className="min-w-0 grow shrink-0 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-600"
                    >
                      Abrir lista
                    </button>
                    <button
                      type="button"
                      onClick={() => setQrPara(f)}
                      className="min-w-0 grow shrink-0 cursor-pointer whitespace-nowrap rounded-md border border-secondary-300 bg-white px-3 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
                    >
                      Ver QR
                    </button>
                  </div>

                  {(podeCancelar || podeConferir) && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {podeCancelar && (
                        <button
                          type="button"
                          onClick={() => setCancelarPara(f)}
                          className="min-w-0 grow shrink-0 cursor-pointer whitespace-nowrap rounded-md border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
                        >
                          Cancelar formação
                        </button>
                      )}
                      {podeConferir && (
                        <button
                          type="button"
                          onClick={() => setConferirPara(f)}
                          className="min-w-0 grow shrink-0 cursor-pointer whitespace-nowrap rounded-md border border-secondary-300 bg-white px-3 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
                        >
                          Fechar conferência
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {qrPara && (
        <ModalQRPresenca
          formacao={qrPara}
          aoFechar={() => setQrPara(null)}
          aoGerarNovo={() => setPedindoNovo(qrPara)}
        />
      )}

      {pedindoNovo && (
        <ModalConfirmarAcao
          titulo="Gerar QR novo"
          mensagem="O QR antigo para de valer. Tem certeza?"
          textoConfirmar="Gerar novo"
          salvando={gerando}
          aoConfirmar={gerarNovoQR}
          aoCancelar={() => setPedindoNovo(null)}
        />
      )}

      {cancelarPara && (
        <ModalConfirmarAcao
          titulo="Cancelar formação"
          mensagem="O QR para de valer na hora e ninguém mais consegue marcar presença. Tem certeza?"
          textoConfirmar="Cancelar formação"
          salvando={cancelando}
          aoConfirmar={cancelarFormacao}
          aoCancelar={() => setCancelarPara(null)}
        />
      )}

      {conferirPara && (
        <ModalConfirmarAcao
          titulo="Fechar conferência"
          mensagem="A conferência desta formação será encerrada. Tem certeza?"
          textoConfirmar="Fechar"
          salvando={conferindo}
          aoConfirmar={conferirFormacao}
          aoCancelar={() => setConferirPara(null)}
        />
      )}
    </div>
  );
}